import * as React from "react";

import { Box, Button, CircularProgress, Grid, useTheme } from "@mui/material";
import { GridMetric } from "../general/custom";
import { fetchDeviceDetail } from "../../scripts/api_v2/commands_client";

import { formatTime } from "../../scripts/utils";
import { SubTitle } from "../general/typography";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

function formatDate(d) {
  return (
    ("0" + d.getDate()).slice(-2) +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    d.getFullYear() +
    " " +
    ("0" + d.getHours()).slice(-2) +
    ":" +
    ("0" + d.getMinutes()).slice(-2) +
    ":" +
    ("0" + d.getSeconds()).slice(-2)
  );
}

function formatValue(v) {
  if (v === "nan" || v === undefined) return "-";
  else if (Array.isArray(v)) return v.reduce((sum, a) => sum + a, 0);
  return v;
}

function RegisterLog({ name, logs }) {
  const theme = useTheme();
  const [showWriteLog, setShowWriteLog] = React.useState(false);
  const tableBodyRef = React.useRef(null);

  // Scroll to the top of the log box whenever logs are updated
  // const scrollToTop = () => {
  //     if (tableBodyRef.current) {
  //         tableBodyRef.current.scrollIntoView({ behavior: 'smooth' }); // Scroll smoothly to the first log
  //     }
  // };

  // Effect to scroll to the top whenever logs are updated
  // React.useEffect(() => {
  //     if (!isPaused) {
  //         scrollToTop();
  //     }
  // }, [writes]);

  const toggleShow = () => {
    setShowWriteLog((prev) => !prev);
  };

  function LogEntry(entry, index) {
    const d = new Date(entry[0] * 1000);
    const log = entry[1];
    return (
      <tr style={{ width: "100%" }} key={index}>
        <td style={{ width: "30%", textAlign: "left", padding: "5px" }}>
          <b style={{ color: theme.palette.secondary.main }}>{formatDate(d)}</b>
        </td>
        <td style={{ width: "20%", padding: "5px 20px", whiteSpace: "nowrap", textAlign: "left" }}>
          <b>[{log.address}]</b>
        </td>
        <td style={{ width: "20%", padding: "5px 20px", whiteSpace: "nowrap", textAlign: "left" }}>
          <b>{log.name}</b>
        </td>
        <td style={{ width: "30%", textAlign: "right", padding: "5px" }}>
          <b>{Math.round((log.value + Number.EPSILON) * 1000) / 1000}</b>
        </td>
      </tr>
    );
  }

  function LogTable() {
    return (
      <>
        {" "}
        {logs ? (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ width: "30%", textAlign: "left", padding: "10px" }}>Time</th>
                <th style={{ width: "20%", textAlign: "left", padding: "5px" }}>Address</th>
                <th style={{ width: "20%", textAlign: "left", padding: "5px" }}>Name</th>
                <th style={{ width: "30%", textAlign: "right", padding: "10px" }}>Value</th>
              </tr>
            </thead>
            <tbody style={{ overflowY: "auto", height: "400px" }}>
              {logs.map((entry, index) => LogEntry(entry, index))}
            </tbody>
          </table>
        ) : (
          "Feature not available on current version or log currently not available"
        )}
      </>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header} ref={tableBodyRef}>
        <SubTitle py={2}>{name}</SubTitle>
        <Button
          sx={{ m: 1, alignSelf: "center" }}
          onClick={() => {
            toggleShow();
          }}
        >
          {showWriteLog ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Button>
      </div>
      {showWriteLog && <LogTable />}
    </div>
  );
}

export function RegisterLiveData({ deviceId }) {
  const params = useParams();
  const backend = useBackend();
  const theme = useTheme();
  const tableBodyRef = React.useRef(null);
  const [isPaused, setIsPaused] = React.useState(false);

  const {
    data: data,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.DEVICE_DETAIL, backend.fetch, params.site_id, deviceId],
    queryFn: async () => {
      return await fetchDeviceDetail(backend.fetch, params.site_id, deviceId);
    },
    refetchInterval: 5000,
  });

  const timestamp = data && data.registers ? new Date().getTime() / 1000 : null;

  return (
    <Box alignSelf="flex-start">
      <Grid container>
        {data && data.registers ? (
          data.registers.map((reg) => (
            <GridMetric key={reg.name} name={reg.name + " [" + reg.address + "]"} value={formatValue(reg.value)} />
          ))
        ) : (
          <Box style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}
        {timestamp ? <GridMetric name={"reading time"} value={formatTime(new Date(timestamp * 1000))} /> : null}
      </Grid>
      <RegisterLog name="Register write log" logs={data?.register_writes} />
      <RegisterLog name="Register read log" logs={data?.register_reads} />
    </Box>
  );
}

// Styling for the component
const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    margin: "0px 0",
  },
  logBox: {
    width: "100%",
    height: "400px",
    overflowY: "auto",
    backgroundColor: "#fff",
    padding: "10px",
    display: "flex",
    flexDirection: "column", // Ensure the newest log stays at the top
  },
  logEntry: {
    padding: "5px 0",
    borderBottom: "1px solid #eee",
  },
};
